import useLenis from "@/hooks/useLenis";
import Layout from "@/components/Layout";
import Transparency from "@/components/Transparency";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function Philanthropy() {
  useLenis();
  return (
    <Layout>
      <div data-testid="page-philanthropy" className="relative bg-[#050505] text-[#ededed] min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 sm:pt-40 pb-14 sm:pb-20 px-6 sm:px-10 max-w-[1200px] mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]"
          >
            The Public Record
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            data-testid="philanthropy-headline"
            className="font-display uppercase leading-[0.95] text-[#ededed] mt-6"
            style={{ fontSize: "clamp(2.75rem, 8vw, 6.5rem)", letterSpacing: "-0.005em" }}
          >
            Philanthropy.
            <br />
            <span className="italic text-[#ff5a00]">Loud on the record.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] max-w-3xl mx-auto mt-8"
          >
            The pledge is named. The causes are named. The receipts follow.
          </motion.p>
        </section>

        {/* Family loop — Founder plain-ink 2026-08-17: "The Three Houses, One
            Door section should be at the VERY top of the philanthropy page,
            list that first." Sits directly under the hero, before the
            pledge card and sibling detail blocks. */}
        <section
          data-testid="family-loop"
          className="px-6 sm:px-10 max-w-[1400px] mx-auto pt-2"
        >
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] text-center"
          >
            The Family Loop
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="family-loop-headline"
            className="font-display uppercase text-center text-[#ededed] mt-4"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
          >
            Three houses, one door each.
          </motion.h2>
          <p className="text-center font-display italic text-lg text-[#a1a1aa] max-w-2xl mx-auto mt-4">
            Every sibling's philanthropy is on the record. Cross-link only.
            Zero paid media, family-wide.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {/* Card 1 — Resilience (self) */}
            <div
              data-testid="family-card-resilience"
              className="border border-[#ff5a00]/40 bg-gradient-to-br from-[#12100c] to-[#0b0b0d] px-6 py-8 relative"
            >
              <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.32em] text-[#ff5a00]">
                You are here
              </span>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
                Resilience Studios
              </p>
              <h3
                className="font-display uppercase text-[#ededed] mt-3 leading-[0.95]"
                style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)" }}
              >
                The Umbrella <span className="text-[#ff5a00]">Pledge</span>
              </h3>
              <p className="font-body text-sm text-[#a1a1aa] mt-4 leading-relaxed">
                10% of net revenue, every year, named causes on the record.
                The percent and the causes are public. The dollar amounts sit with the studio.
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
                resiliencemultimediastudios.com / philanthropy
              </p>
            </div>

            {/* Card 2 — Baker & Sage */}
            <a
              href="https://bakerandsage.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="family-card-sage"
              className="group border border-[#27272a] bg-[#0b0b0d]/70 px-6 py-8 hover:border-[#3DE0C8]/60 transition-colors block"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#3DE0C8]">
                Baker &amp; Sage
              </p>
              <h3
                className="font-display uppercase text-[#ededed] mt-3 leading-[0.95] group-hover:text-[#3DE0C8] transition-colors"
                style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)" }}
              >
                The <span className="text-[#3DE0C8]">50 / 25 / 20 / 5</span> Split
              </h3>
              <p className="font-body text-sm text-[#a1a1aa] mt-4 leading-relaxed">
                Users take the majority slice. Resilience runs a modest cut for
                operating capital. A fifth of every dollar is philanthropy at
                the door. A whistleblower fund keeps the last five cents honest.
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] group-hover:text-[#3DE0C8] transition-colors">
                bakerandsage.com &nbsp;→
              </p>
            </a>

            {/* Card 3 — Aethelgard */}
            <a
              href="https://aethelgardthelivingworlds.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="family-card-aethelgard"
              className="group border border-[#27272a] bg-[#0b0b0d]/70 px-6 py-8 hover:border-[#c9a45c]/60 transition-colors block"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#c9a45c]">
                Aethelgard
              </p>
              <h3
                className="font-display uppercase text-[#ededed] mt-3 leading-[0.95] group-hover:text-[#c9a45c] transition-colors"
                style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)" }}
              >
                A Fifty-Mechanic <span className="text-[#c9a45c]">Living World</span>
              </h3>
              <p className="font-body text-sm text-[#a1a1aa] mt-4 leading-relaxed">
                41% player-owned. It pays its players. Hero-mints, overflow
                splits, quests-become-donations, and a public wall.
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] group-hover:text-[#c9a45c] transition-colors">
                aethelgardthelivingworlds.com &nbsp;→
              </p>
            </a>
          </div>
        </section>

        <div className="mt-20 pt-14 border-t border-white/[0.05]" />

        <Transparency compact />

        {/* Sibling philanthropy models. Moved off /mission per Founder plain-ink
            2026-08-15 — mission is the quote + short paragraph, philanthropy is
            here. Renders alongside Umbrella's own pledge as detailed blocks. */}
        <section
          data-testid="sibling-philanthropy"
          className="px-6 sm:px-10 max-w-[1000px] mx-auto mt-24 pt-16 border-t border-white/[0.05] space-y-14"
        >
          {/* Resilience own block — carries the "much more" list of philanthropy
              mechanics beyond the 10% pledge. Content sourced from the birth
              scroll (Laws 2, 3, 4, 5) and the /philanthropy ledger. Founder
              plain-ink 2026-02-15: "you're still missing a section that was in
              philanthropy regarding inventions, discoveries, etc. The 'much
              more' section for Resilience." */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="philanthropy-resilience-block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-3">
              House · Resilience Studios · Philanthropy Model
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] mb-4">
              Resilience — the 10% floor and everything under it.
            </h3>
            <p className="font-body text-[#a1a1aa] leading-relaxed">
              The public number is the pledge — <strong className="text-[#ff5a00]">10% of net revenue</strong> to
              the named causes above, every year, on the record. The pledge is
              the floor; underneath it, the house runs a working set of
              mechanics that route real dollars, real land, and real work back
              out to Earth, her people, and her animals.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3 font-body text-[#a1a1aa]">
              <li><strong className="text-[#ff5a00]">The Overflow Split</strong> — every sibling house in the umbrella ships with a Founder Ceiling. Every dollar the Founder earns above the ceiling of a sibling house splits 50 / 50 between Resilience Studios (strategic reservoir) and direct philanthropy. The ceiling is the point.</li>
              <li><strong className="text-[#ff5a00]">Land-then-Charity-then-Contract</strong> — the master mechanic. As revenue grows, Resilience target-buys two adjacent plots per county, cheapest counties first: one for asset holding, one for the homeless project on top of it. The homeless project is funded by the philanthropy arm and built on Resilience-owned land, contracted through Resilience or her workers. Generosity is retained; the asset compounds; in downturns the paired plots sell fast at a mark-down.</li>
              <li><strong className="text-[#ff5a00]">Loud on Collapse</strong> — cost, time, and staff-collapse metrics for every traditional industry the studio makes obsolete are broadcast at maximum volume on the public wire (`/api/transparency/collapse`). The money is the quiet half of transparency; the receipt of what was collapsed is the loud half.</li>
              <li><strong className="text-[#ff5a00]">Direct Homeless Housing</strong> — the primary named cause on the pledge ledger. Same-day funding routes into the paired-plot homeless projects above, so a dollar named "homelessness" on the wire lands on a plot Resilience already owns, built by hands Resilience already contracts.</li>
              <li><strong className="text-[#ff5a00]">The Family Wire</strong> — every philanthropy scroll Resilience files (`/api/scrolls`) is public and readable by every sibling under the shared family token. Sage and Aethelgard mirror it into their own houses. Cost per house: zero. Reach per house: three-times, without a single dollar of paid media.</li>
            </ul>
            <p className="font-body text-[#a1a1aa] mt-3">
              ... And much more.
            </p>
            <p
              className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#ff5a00] mt-6 pt-4 border-t border-white/[0.06]"
              data-testid="philanthropy-resilience-catalog-note"
            >
              Founder-adjustable · never below the 10% floor · causes named, not counted.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="philanthropy-sage-block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#3DE0C8] mb-3">
              Sibling House ·{" "}
              <a
                href="https://bakerandsage.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="philanthropy-sage-link"
                className="hover:text-[#ededed] underline decoration-[#3DE0C8]/40 hover:decoration-[#ededed] transition-colors"
              >
                Baker &amp; Sage
              </a>{" "}
              · Philanthropy Model
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] mb-4">
              Sage — the 50 / 25 / 20 / 5 Split
            </h3>
            <p className="font-body text-[#a1a1aa] leading-relaxed">
              Sage funnels every dollar she generates through a four-part split.
              Users take the majority slice; Resilience runs a modest cut for
              operating capital; a fifth of every dollar is philanthropy at the
              door; and a whistleblower fund keeps the last five cents honest.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3 font-body text-[#a1a1aa]">
              <li><strong className="text-[#3DE0C8]">50%</strong> to Sage's users — the creators building on Sage's tools take the majority slice on every dollar their work moves.</li>
              <li><strong className="text-[#3DE0C8]">25%</strong> to Resilience Multimedia Studios' operating wallet — the uncapped fund for hunger-and-homelessness pilots at scale, genius grants, and endeavors the family has not yet imagined.</li>
              <li><strong className="text-[#3DE0C8]">20%</strong> to philanthropy at the door — split across homelessness, hunger, disaster relief, and children's hospitals. Same-day, source-attributed transfer.</li>
              <li><strong className="text-[#3DE0C8]">5%</strong> to a legal whistleblower fund — retained counsel for anyone in the family (or outside it) who needs to safely put a truth on the record.</li>
            </ul>
            <p className="font-body text-[#a1a1aa] mt-3">
              Every philanthropic transaction is a same-day, source-attributed
              feed on Sage's public glass ledger. No commingling.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="philanthropy-aethelgard-block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#c9a45c] mb-3">
              Sibling House ·{" "}
              <a
                href="https://aethelgardthelivingworlds.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="philanthropy-aethelgard-link"
                className="hover:text-[#ededed] underline decoration-[#c9a45c]/40 hover:decoration-[#ededed] transition-colors"
              >
                Aethelgard
              </a>{" "}
              · Philanthropy Model
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] mb-4">
              Aethelgard — A Fifty-Mechanic Philanthropy
            </h3>
            <p className="font-body text-[#a1a1aa] leading-relaxed">
              Aethelgard turns the philanthropy mechanic into a gameplay mechanic —
              across roughly fifty distinct layers. The house is{" "}
              <strong className="text-[#c9a45c]">41% player-owned</strong>, and{" "}
              <strong className="text-[#c9a45c]">it pays its players</strong> — the
              two loudest lines in an ecosystem that also runs Hero-Mints, overflow-
              to-cause splits, gameplay-quests-become-donations, public philanthropic
              walls, and{" "}
              <strong className="text-[#c9a45c]">The Glass Ledger</strong> — a
              public, on-chain record of every philanthropic dollar routed, cause
              named, and recipient reached, live and unhideable.
            </p>
            <p className="font-body text-[#a1a1aa] mt-3">
              ... And much more.
            </p>
            <p
              className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#c9a45c] mt-6 pt-4 border-t border-white/[0.06]"
              data-testid="philanthropy-aethelgard-catalog-note"
            >
              Note — this same model holds across all 24 gacha game stacks in the
              Resilience Multimedia Studios catalog.
            </p>
          </motion.div>
        </section>

        {/* Back-link */}
        <section className="px-6 sm:px-10 max-w-[1200px] mx-auto pb-24 text-center">
          <Link
            to="/"
            data-testid="philanthropy-back-home"
            className="inline-block font-mono text-[10px] uppercase tracking-[0.42em] text-[#a1a1aa] hover:text-[#ff5a00] transition-colors border border-[#27272a] hover:border-[#ff5a00]/60 px-6 py-3"
          >
            ← Back to the Forge
          </Link>
        </section>
      </div>
    </Layout>
  );
}
